import { Switch, Route, useLocation, Redirect } from "wouter";
import { PrivatePropertyBetaRoutes } from "@/components/beta-feature-route";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { MiniTourProvider } from "@/components/tour/MiniTourProvider";
import { AnthemProvider } from "@/components/anthem-provider";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider, useTheme } from "@/components/theme-provider";
import { ErrorBoundary } from "@/components/error-boundary";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { DemoBanner } from "@/components/DemoBanner";
import { useEffect, useRef } from "react";
import Admin from "@/pages/admin";
import AdminSetupPassword from "@/pages/admin-setup-password";
import AdminPulse from "@/pages/admin-pulse";
import BlogPage from "@/pages/blog";
import BlogPostPage from "@/pages/blog-post";
import FeatureRequestsPage from "@/pages/feature-requests";
import PrivacyPage from "@/pages/privacy";
import TermsPage from "@/pages/terms";
import AppLogin from "@/pages/app-login";
import AppVerify from "@/pages/app-verify";
import AppTotpVerify from "@/pages/app-totp-verify";
import AppTotpEnroll from "@/pages/app-totp-enroll";
import AppAwaitingApproval from "@/pages/app-awaiting-approval";
import AppOnboardingProfile from "@/pages/app-onboarding-profile";
import AppOnboardingPayment from "@/pages/app-onboarding-payment";
import AppWelcome from "@/pages/app-welcome";
import AppNoTeam from "@/pages/app-no-team";
import AppAcceptInvitations from "@/pages/app-accept-invitations";
import AppProperties from "@/pages/app-properties";
import AppPropertyDetail from "@/pages/app-property-detail";
import AppMessages from "@/pages/app-messages";
import AppMessagesPending from "@/pages/app-messages-pending";
import AppPartnerConversations from "@/pages/app-partner-conversations";
import AppConnections from "@/pages/app-connections";
import AppPricing from "@/pages/app-pricing";
import AppPricingModelCreate from "@/pages/app-pricing-model-create";
import AppSetup from "@/pages/app-setup";
import AppUnderwriting from "@/pages/app-underwriting";
import AppUnderwritingDetail from "@/pages/app-underwriting-detail";
import AppUnderwritingDefaults from "@/pages/app-underwriting-defaults";
import AppImageGenerator from "@/pages/app-image-generator";
import AppPromptLibrary from "@/pages/app-prompt-library";
import AppTeam from "@/pages/app-team";
import AppTasks from "@/pages/app-tasks";
import AppMaintenanceSettings from "@/pages/app-maintenance-settings";
import AppDevicesOverview from "@/pages/app-devices-overview";
import AppDevicesSmartLocks from "@/pages/app-devices-smart-locks";
import AppDevicesThermostats from "@/pages/app-devices-thermostats";
import AppMembers from "@/pages/app-members";
import AppMembersBookingRequests from "@/pages/app-members-booking-requests";
import AppMembersBookingRequestsSettings from "@/pages/app-members-booking-requests-settings";
import BookingRequestPage from "@/pages/booking-request";
import AppMemberDetail from "@/pages/app-member-detail";
import AppVehicles from "@/pages/app-vehicles";
import AppOperationsVendors from "@/pages/app-operations-vendors";
import AppOperationsVendorDetail from "@/pages/app-operations-vendor-detail";
import AppOperationsVendorSettings from "@/pages/app-operations-vendor-settings";
import AppOperationsServiceForms from "@/pages/app-operations-service-forms";
import ServiceFormPage from "@/pages/service-form";
import { PublicMemberCleaning } from "@/pages/member-portal/MemberCleaning";
import AppOperationsApprovals from "@/pages/app-operations-approvals";
import AppOperationsTaskRules from "@/pages/app-operations-task-rules";
import AppCleaning from "@/pages/app-cleaning";
import AppOperationsTrash from "@/pages/app-operations-trash";
import AppOperationsTodos from "@/pages/app-operations-todos";
import AppOperationsMarketplace from "@/pages/app-operations-marketplace";
import AppOperationsPayments from "@/pages/app-operations-payments";
import VendorPortal from "@/pages/vendor-portal";
import AppFinancials from "@/pages/app-financials";
import AppPortfolioAnalytics from "@/pages/app-portfolio-analytics";
import AppTeamStats from "@/pages/app-team-stats";
import AppProfile from "@/pages/app-profile";
import AppRefer from "@/pages/app-refer";
import AppAccountBilling from "@/pages/app-account-billing";
import AppResourceMercury from "@/pages/app-resource-mercury";
import AppAccountPlan from "@/pages/app-account-plan";
import AppPadsplitSync from "@/pages/app-padsplit-sync";
import AppConnectPadsplit from "@/pages/app-connect-padsplit";
import AppReviews from "@/pages/app-reviews";
import AppDashboard from "@/pages/app-dashboard";
import AppDailyPulse from "@/pages/app-daily-pulse";
import AppMessageRuleForm from "@/pages/app-message-rule-form";
import AppFurnishingCatalog from "@/pages/app-furnishing-catalog";
import AppFurnishingProperty from "@/pages/app-furnishing-property";
import AppFurnishingAddressBook from "@/pages/app-furnishing-address-book";
import AppPropertyLaunch from "@/pages/app-property-launch";
import AppRenovations from "@/pages/app-renovations";
import AppBuy from "@/pages/app-buy";
import AppBuyDetail from "@/pages/app-buy-detail";
import AppFindRealtor from "@/pages/app-find-realtor";
import AppRealtorListingDetail from "@/pages/app-realtor-listing-detail";
import AppWholesalerListingDetail from "@/pages/app-wholesaler-listing-detail";
import AppAcquisitions from "@/pages/app-acquisitions";
import AppAcquisitionDetail from "@/pages/app-acquisition-detail";
import AppResearch from "@/pages/app-research";
import AppResearchCity from "@/pages/app-research-city";
import AppBuyBoxSettings from "@/pages/app-buy-box-settings";
import AppBuyBoxReports from "@/pages/app-buy-box-reports";
import AppBuyBoxSaved from "@/pages/app-buy-box-saved";
import AppBuyBoxMatches from "@/pages/app-buy-box-matches";
import AppBuyBoxAgents from "@/pages/app-buy-box-agents";
import AppFinance from "@/pages/app-finance";
import AppCollections from "@/pages/app-collections";
import AppSuppliesPage from "@/pages/app-supplies-page";
import AppMarketingDirectBookings from "@/pages/app-marketing-direct-bookings";
import AppMarketingThirdPartyListings from "@/pages/app-marketing-third-party-listings";
import AppSell from "@/pages/app-sell";
import AppSellDetail from "@/pages/app-sell-detail";
import AppFaqPage from "@/pages/app-faq";
import AppAccountKnowledgeBase from "@/pages/app-account-knowledge-base";
import AppAccountMessaging from "@/pages/app-account-messaging";
import AppMessagesSettings from "@/pages/app-messages-settings";
import AppMessagesAutoDraft from "@/pages/app-messages-auto-draft";
import AppMessagesUnmatchedSms from "@/pages/app-messages-unmatched-sms";
import AppAccountPmBilling from "@/pages/app-account-pm-billing";
import AppOperationsOwners from "@/pages/app-operations-owners";
import AppOperationsOwnerInvoices from "@/pages/app-operations-owner-invoices";
import RoomsForRent from "@/pages/rooms-for-rent";
import NotFound from "@/pages/not-found";
import AppDevToolsImpersonate from "@/pages/app-dev-tools-impersonate";
import AppDevToolsLog from "@/pages/app-dev-tools-log";
import AppDevToolsAiPlayground from "@/pages/app-dev-tools-ai-playground";
import PartnerLendersPage from "@/pages/partners-lenders";
import PartnerWholesalersPage from "@/pages/partners-wholesalers";
import PartnerRealtorsPage from "@/pages/partners-realtors";
import PartnersHubPage from "@/pages/partners-hub";
import PartnerBrokersPage from "@/pages/partners-brokers";
import AboutPadsplit from "@/pages/about-padsplit";
import PricingPage from "@/pages/pricing";
import CertifiedPropertyManagersPage from "@/pages/certified-property-managers";
import RecommendedFurnishingsPage from "@/pages/recommended-furnishings";
import AppAdminRecommendedFurnishings from "@/pages/app-admin-recommended-furnishings";
import AppAdminBetaFeatures from "@/pages/app-admin-beta-features";
import AppAdminConferenceLeads from "@/pages/app-admin-conference-leads";
import AppAdminDashboardLayouts from "@/pages/app-admin-dashboard-layouts";
import PublicFaqHostPage from "@/pages/public-faq-host";
import PublicFaqVendorPage from "@/pages/public-faq-vendor";
import FeaturesGroupPage from "@/pages/features-group";
import FeaturesSubfeaturePage from "@/pages/features-subfeature";
import FeaturesDailyPulsePage from "@/pages/features-daily-pulse";
import SavingsCalculatorPage from "@/pages/savings-calculator";
import CertifiedPropertyManagerPage from "@/pages/certified-property-manager";
import SignupPage from "@/pages/signup";
import HomePage, { HomeV2Preview, HomeV3Preview } from "@/pages/home";
import CoreFeaturePage from "@/pages/core-feature";
import FeaturesAllPage from "@/pages/features-all";
import PropertyManagersPage from "@/pages/property-managers";
import ResearchLandingPage from "@/pages/research-landing";
import { CORE_PAGES } from "@/data/core-pages";
import SyrePage from "@/pages/syre";
import LifetimeClaim from "@/pages/lifetime-claim";
import { MARKETING_REDIRECTS, withQuery } from "@/data/redirects";

/** Client-side half of the retired-URL redirects (the server sends 301s); keeps the query string. */
function RetiredPageRedirect({ to }: { to: string }) {
  return <Redirect to={withQuery(to, window.location.search)} replace />;
}


function Router() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  /*
    Some adjacent destinations intentionally use the same page component
    (for example, Financials' path-based tabs). Give the route outlet a
    pathname key so a path navigation cannot reconcile the old route tree
    in place and leave the previous submenu page visible while the URL has
    already changed. Query-string tab changes stay in-place because wouter's
    location value excludes the search string.
  */
  return (
    <PrivatePropertyBetaRoutes>
    <Switch key={location}>
      <Route path="/" component={HomePage} />
      <Route path="/v2" component={HomeV2Preview} />
      <Route path="/v3" component={HomeV3Preview} />
      <Route path="/property-managers" component={PropertyManagersPage} />
      <Route path="/research" component={ResearchLandingPage} />
      <Route path="/admin" component={Admin} />
      {/* Per-record deep link used by Slack signup notifications and admin
          emails ("View in Admin Dashboard"). It was sent for years without a
          matching route (404) — redirect into the app-users tab and focus the
          row so every historical link works too. */}
      <Route path="/admin/users/:id">
        {(params) => <Redirect to={`/admin?tab=app-users&focusUser=${encodeURIComponent(params.id)}`} replace />}
      </Route>
      <Route path="/admin/pulse" component={AdminPulse} />
      <Route path="/admin/setup-password" component={AdminSetupPassword} />
      <Route path="/blog" component={BlogPage} />
      <Route path="/blog/:id" component={BlogPostPage} />
      <Route path="/feature-requests" component={FeatureRequestsPage} />
      <Route path="/savings-calculator" component={SavingsCalculatorPage} />
      <Route path="/certified-property-manager" component={CertifiedPropertyManagerPage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/syre" component={SyrePage} />
      <Route path="/app/lifetime-claim" component={LifetimeClaim} />
      {MARKETING_REDIRECTS.map((r) => (
        <Route key={r.from} path={r.from}>{() => <RetiredPageRedirect to={r.to} />}</Route>
      ))}
      <Route path="/certified-property-managers" component={CertifiedPropertyManagersPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/about" component={AboutPadsplit} />
      <Route path="/vendor-portal/:vendorId" component={VendorPortal} />
      <Route path="/vendor-portal" component={VendorPortal} />
      <Route path="/app/login" component={AppLogin} />
      <Route path="/app/verify" component={AppVerify} />
      <Route path="/app/totp-verify" component={AppTotpVerify} />
      <Route path="/app/totp-enroll" component={AppTotpEnroll} />
      <Route path="/app/awaiting-approval" component={AppAwaitingApproval} />
      <Route path="/app/no-team" component={AppNoTeam} />
      <Route path="/app/accept-invitations" component={AppAcceptInvitations} />
      <Route path="/app/properties" component={AppProperties} />
      <Route path="/app/properties/:id" component={AppPropertyDetail} />
      <Route path="/app/underwriting/defaults" component={AppUnderwritingDefaults} />
      <Route path="/app/underwriting/:id" component={AppUnderwritingDetail} />
      <Route path="/app/underwriting" component={AppUnderwriting} />
      <Route path="/app/messages/rules/new" component={AppMessageRuleForm} />
      <Route path="/app/messages/rules/:id" component={AppMessageRuleForm} />
      <Route path="/app/messages/pending" component={AppMessagesPending} />
      <Route path="/app/messages/settings" component={AppMessagesSettings} />
      <Route path="/app/messages/auto-draft" component={AppMessagesAutoDraft} />
      <Route path="/app/messages/unmatched-sms" component={AppMessagesUnmatchedSms} />
      <Route path="/app/messages" component={AppMessages} />
      <Route path="/app/partner-conversations" component={AppPartnerConversations} />
      <Route path="/app/connections" component={AppConnections} />
      <Route path="/app/resources/mercury" component={AppResourceMercury} />
      <Route path="/app/pricing/models/new" component={AppPricingModelCreate} />
      <Route path="/app/pricing/models/:id/edit" component={AppPricingModelCreate} />
      <Route path="/app/pricing/models" component={AppPricing} />
      <Route path="/app/pricing" component={AppPricing} />
      <Route path="/app/setup" component={AppSetup} />
      <Route path="/app/image-generator" component={AppImageGenerator} />
      <Route path="/app/prompt-library" component={AppPromptLibrary} />
      <Route path="/app/team" component={AppTeam} />
      <Route path="/app/reviews" component={AppReviews} />
      <Route path="/app/tasks/settings" component={AppMaintenanceSettings} />
      <Route path="/app/tasks" component={AppTasks} />
      <Route path="/app/maintenance/settings">{() => { window.location.replace("/app/tasks/settings"); return null; }}</Route>
      <Route path="/app/maintenance">{() => { const search = window.location.search || ""; window.location.replace(`/app/tasks${search}`); return null; }}</Route>
      <Route path="/app/supplies" component={AppSuppliesPage} />
      <Route path="/app/operations/vendors/settings" component={AppOperationsVendorSettings} />
      <Route path="/app/operations/vendors/:id" component={AppOperationsVendorDetail} />
      <Route path="/app/operations/vendors" component={AppOperationsVendors} />
      <Route path="/app/operations/service-forms" component={AppOperationsServiceForms} />
      <Route path="/service-form/:token" component={ServiceFormPage} />
      <Route path="/cleaning/:token">
        {(params) => <PublicMemberCleaning token={params.token} />}
      </Route>
      <Route path="/app/operations/approvals" component={AppOperationsApprovals} />
      <Route path="/app/operations/task-rules" component={AppOperationsTaskRules} />
      <Route path="/app/cleaning" component={AppCleaning} />
      <Route path="/app/operations/marketplace" component={AppOperationsMarketplace} />
      <Route path="/app/operations/marketplace/:vendorId" component={AppOperationsMarketplace} />
      <Route path="/app/operations/payments" component={AppOperationsPayments} />
      <Route path="/app/operations/trash" component={AppOperationsTrash} />
      <Route path="/app/operations/todos" component={AppOperationsTodos} />
      <Route path="/app/devices/smart-locks">{() => <AppDevicesSmartLocks />}</Route>
      <Route path="/app/devices/thermostats">{() => <AppDevicesThermostats />}</Route>
      <Route path="/app/devices">{() => <AppDevicesOverview />}</Route>
      <Route path="/app/members/vehicles" component={AppVehicles} />
      <Route path="/app/members/booking-requests/settings" component={AppMembersBookingRequestsSettings} />
      <Route path="/app/members/booking-requests" component={AppMembersBookingRequests} />
      <Route path="/booking-request/:token" component={BookingRequestPage} />
      <Route path="/app/members/:id" component={AppMemberDetail} />
      <Route path="/app/members" component={AppMembers} />
      <Route path="/app/profile" component={AppProfile} />
      <Route path="/app/refer" component={AppRefer} />
      <Route path="/app/account/billing" component={AppAccountBilling} />
      <Route path="/app/account/plan" component={AppAccountPlan} />
      <Route path="/app/account/pm-billing" component={AppAccountPmBilling} />
      <Route path="/app/account/knowledge-base" component={AppAccountKnowledgeBase} />
      <Route path="/app/account/messaging" component={AppAccountMessaging} />
      <Route path="/app/operations/owners" component={AppOperationsOwners} />
      <Route path="/app/operations/owner-invoices" component={AppOperationsOwnerInvoices} />
      <Route path="/app/financials/collections" component={AppCollections} />
      <Route path="/app/financials/:tab" component={AppFinancials} />
      <Route path="/app/financials" component={AppFinancials} />
      <Route path="/app/analytics" component={AppPortfolioAnalytics} />
      <Route path="/app/team-stats" component={AppTeamStats} />
      <Route path="/app/padsplit-sync" component={AppPadsplitSync} />
      <Route path="/app/connect-padsplit" component={AppConnectPadsplit} />
      <Route path="/app/property-launch/:id" component={AppPropertyLaunch} />
      <Route path="/app/property-launch" component={AppPropertyLaunch} />
      <Route path="/app/buy/wholesaler/:id" component={AppWholesalerListingDetail} />
      <Route path="/app/buy/:id" component={AppBuyDetail} />
      <Route path="/app/realtor-listings/:id" component={AppRealtorListingDetail} />
      <Route path="/app/buy" component={AppBuy} />
      <Route path="/app/find-realtor" component={AppFindRealtor} />
      <Route path="/app/acquisitions/:id" component={AppAcquisitionDetail} />
      <Route path="/app/acquisitions" component={AppAcquisitions} />
      <Route path="/app/research/:slug" component={AppResearchCity} />
      <Route path="/app/buy-box/settings" component={AppBuyBoxSettings} />
      <Route path="/app/buy-box/saved" component={AppBuyBoxSaved} />
      <Route path="/app/buy-box/matches" component={AppBuyBoxMatches} />
      <Route path="/app/buy-box/agents" component={AppBuyBoxAgents} />
      <Route path="/app/buy-box/reports/:id" component={AppBuyBoxReports} />
      <Route path="/app/buy-box/reports" component={AppBuyBoxReports} />
      <Route path="/app/finance" component={AppFinance} />
      <Route path="/app/research" component={AppResearch} />
      <Route path="/app/renovations/:id" component={AppRenovations} />
      <Route path="/app/renovations" component={AppRenovations} />
      <Route path="/app/onboarding" component={AppOnboardingProfile} />
      <Route path="/app/onboarding/payment" component={AppOnboardingPayment} />
      <Route path="/app/welcome" component={AppWelcome} />
      <Route path="/app/daily-pulse" component={AppDailyPulse} />
      <Route path="/app/dashboard" component={AppDashboard} />
      <Route path="/app" component={AppDashboard} />
      <Route path="/app/furnishing-catalog" component={AppFurnishingCatalog} />
      <Route path="/app/furnishing-address-book" component={AppFurnishingAddressBook} />
      <Route path="/app/properties/:id/furnishing" component={AppFurnishingProperty} />
      <Route path="/app/marketing/direct-bookings" component={AppMarketingDirectBookings} />
      <Route path="/app/marketing/third-party-listings" component={AppMarketingThirdPartyListings} />
      <Route path="/app/sell" component={AppSell} />
      <Route path="/app/sell/:id" component={AppSellDetail} />
      <Route path="/app/buy/listing/:id" component={AppSellDetail} />
      <Route path="/app/dev-tools/impersonate" component={AppDevToolsImpersonate} />
      <Route path="/app/dev-tools/log" component={AppDevToolsLog} />
      <Route path="/app/dev-tools/ai-playground" component={AppDevToolsAiPlayground} />
      <Route path="/app/faq" component={AppFaqPage} />
      <Route path="/rooms-for-rent/:slug" component={RoomsForRent} />
      <Route path="/rooms-for-rent" component={RoomsForRent} />
      <Route path="/partners" component={PartnersHubPage} />
      <Route path="/partners/lenders" component={PartnerLendersPage} />
      <Route path="/partners/wholesalers" component={PartnerWholesalersPage} />
      <Route path="/partners/brokers" component={PartnerBrokersPage} />
      <Route path="/partners/realtors" component={PartnerRealtorsPage} />
      <Route path="/recommendedfurnishings" component={RecommendedFurnishingsPage} />
      <Route path="/app/admin/recommended-furnishings" component={AppAdminRecommendedFurnishings} />
      <Route path="/app/admin/beta-features" component={AppAdminBetaFeatures} />
      <Route path="/app/admin/conference-leads" component={AppAdminConferenceLeads} />
      <Route path="/app/admin/dashboard-layouts" component={AppAdminDashboardLayouts} />
      <Route path="/faq/hosts" component={PublicFaqHostPage} />
      <Route path="/faq/vendors" component={PublicFaqVendorPage} />
      <Route path="/features" component={FeaturesAllPage} />
      {CORE_PAGES.map((page) => (
        <Route key={page.path} path={page.path}>{() => <CoreFeaturePage slug={page.slug} />}</Route>
      ))}
      <Route path="/features/daily-pulse" component={FeaturesDailyPulsePage} />
      <Route path="/features/:groupSlug/:featureSlug" component={FeaturesSubfeaturePage} />
      <Route path="/features/:groupSlug" component={FeaturesGroupPage} />
      <Route component={NotFound} />
    </Switch>
    </PrivatePropertyBetaRoutes>
  );
}

const PUBLIC_AUTH_ROUTES = [
  "/app/lifetime-claim",
  "/app/login",
  "/app/verify",
  "/app/totp-verify",
  "/app/totp-enroll",
  "/app/awaiting-approval",
  "/app/onboarding",
  "/app/welcome",
  "/app/accept-invitations",
];

function ThemeSyncer() {
  const { applyTheme, themePreference } = useTheme();
  const [location] = useLocation();
  const isPublicRoute = location === "/" || PUBLIC_AUTH_ROUTES.some((r) => location === r || location.startsWith(r + "/"));
  const profileQuery = useQuery<{ preferredTheme?: "light" | "dark" | "system" }>({
    queryKey: ["/api/app/profile"],
    enabled: !isPublicRoute,
  });
  const lastSyncedRef = useRef<string | null>(null);

  useEffect(() => {
    const serverTheme = profileQuery.data?.preferredTheme;
    if (!serverTheme) return;
    if (lastSyncedRef.current === serverTheme) return;
    lastSyncedRef.current = serverTheme;
    if (serverTheme !== themePreference) {
      applyTheme(serverTheme);
    }
  }, [profileQuery.data?.preferredTheme]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeSyncer />
          <TooltipProvider>
            <Toaster />
            <DemoBanner />
            <ImpersonationBanner />
            <MiniTourProvider>
              <AnthemProvider>
                <Router />
              </AnthemProvider>
            </MiniTourProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
